import piexif from "piexifjs";
import { useState } from "react"; // import useRef
import PropTypes from "prop-types";
import {
  Grid,
  Icon,
  Snackbar,
  Alert,
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";

function GeoTaggedImageUploadField({ displayName, fieldRef, isOptional, options }) {
  const [fieldError, showFieldError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [capturedImg, setCapturedImg] = useState(null);
  const [capturedData, setCapturedData] = useState(null);
  const [gpsCoords, setGpsCoords] = useState({});

  function convertDMSToDecimalDegrees(dms, direction) {
    let decimalDegrees;
    if (typeof dms[2] === "undefined") {
      decimalDegrees = dms[0] / dms[1];
    } else {
      decimalDegrees = dms[0][0] + dms[1][0] / 60 + dms[2][0] / 3600;
    }

    // Adjust for direction: South and West are negative
    if (direction === "S" || direction === "W") {
      decimalDegrees *= -1;
    }

    return decimalDegrees.toFixed(6);
  }

  function getHaversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371e3; // Earth's radius in meters (or 3959 for miles)
    const φ1 = (lat1 * Math.PI) / 180; // Latitude 1 in radians
    const φ2 = (lat2 * Math.PI) / 180; // Latitude 2 in radians
    const Δφ = ((lat2 - lat1) * Math.PI) / 180; // Difference in latitude in radians
    const Δλ = ((lon2 - lon1) * Math.PI) / 180; // Difference in longitude in radians

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const d = R * c; // Distance in meters (or miles if R is in miles)
    return d;
  }

  async function toBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  }

  async function handleFileChange(event) {
    const file = event.target.files[0];
    const fileKb = Math.round(file.size / 1024);
    if (fileKb > 5000) {
      setErrorMsg("File size cannot exceed 5MB.");
      showFieldError(true);
      return;
    }

    await toBase64(file)
      .then((res) => {
        if (res.startsWith("data:image/heic;base64") || res.includes("data:image/heic;base64")) {
          setErrorMsg("File format not supported. Recommended image formats: JPEG, JPG, PNG");
          showFieldError(true);
          return;
        }

        let exifObj = null;
        try {
          exifObj = piexif.load(res);
        } catch (e) {
          console.log(e);
          setErrorMsg("Invalid image uploaded. Must be a geo-tagged image.");
          showFieldError(true);
          return;
        }
        // console.log(exifObj);
        
        if (exifObj["Exif"] === "undefined" || exifObj["GPS"] === "undefined") {
          setErrorMsg("Invalid image uploaded. Must be a geo-tagged image.");
          showFieldError(true);
          return;
        }

        if (
          exifObj["GPS"][piexif.GPSIFD.GPSLatitude] === "undefined" ||
          !exifObj["GPS"][piexif.GPSIFD.GPSLatitude]
        ) {
          setErrorMsg("Image has no GPS latitude value.");
          showFieldError(true);
          return;
        }

        if (
          exifObj["GPS"][piexif.GPSIFD.GPSLongitude] === "undefined" ||
          !exifObj["GPS"][piexif.GPSIFD.GPSLatitude]
        ) {
          setErrorMsg("Image has no GPS longitude value.");
          showFieldError(true);
          return;
        }

        var lat = convertDMSToDecimalDegrees(
          exifObj["GPS"][piexif.GPSIFD.GPSLatitude],
          exifObj["GPS"][piexif.GPSIFD.GPSLatitudeRef]
        );

        var lng = convertDMSToDecimalDegrees(
          exifObj["GPS"][piexif.GPSIFD.GPSLongitude],
          exifObj["GPS"][piexif.GPSIFD.GPSLongitudeRef]
        );

        var ts = exifObj["Exif"][piexif.ExifIFD.DateTimeOriginal];

        // Run validation checks if any
        if (
          typeof options.lat !== "undefined" &&
          typeof options.lng !== "undefined" &&
          typeof options.rad !== "undefined"
        ) {
          var dist = getHaversineDistance(
            parseFloat(options.lat).toFixed(6),
            parseFloat(options.lng).toFixed(6),
            lat,
            lng
          );
          // console.log(dist);
          if (dist > parseFloat(options.rad)) {
            setErrorMsg("Image was captured outside GPS location radius.");
            showFieldError(true);
            return;
          }
        }

        const gpsData = {
          latitude: lat,
          longitude: lng,
          timestamp: ts,
        };

        setGpsCoords(gpsData);
        setCapturedImg(res);
        setCapturedData(JSON.stringify({ image: res, gps: gpsData }));
      })
      .catch((err) => {
        setErrorMsg(err);
        showFieldError(true);
        return;
      });
  }

  return (
    <Box>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={5000}
        onClose={() => showFieldError(false)}
        open={fieldError}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Box sx={{ minWidth: 120, width: "100%" }}>
        <Grid container spacing={3}>
          <Grid item xs={6} md={6} lg={6}>
            <Typography variant="h6" fontWeight="light" fontSize="small" mb={2}>
              {displayName}
            </Typography>
          </Grid>
          <Grid item xs={6} md={6} lg={6}>
            <Box align="right">
              {capturedImg && (
                <IconButton
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setCapturedData(null);
                    setCapturedImg(null);
                  }}
                  options={options}
                >
                  <Icon>clear</Icon>
                </IconButton>
              )}
            </Box>
          </Grid>
        </Grid>
        <Box>
          <Box>
            {capturedImg ? (
              <Box mb={3}>
                <img src={capturedImg} alt="webcam" width="50%" />
                <Typography mt={2}>
                  <small>
                    Latitude: {gpsCoords.latitude}, Longitude: {gpsCoords.longitude}
                    &nbsp;
                    <a
                      rel="noreferrer"
                      target="_blank"
                      href={`https://maps.google.com?q=${gpsCoords.latitude},${gpsCoords.longitude}`}
                    >
                      [View On Map]
                    </a>
                  </small>
                </Typography>
              </Box>
            ) : (
              <Box>
                <TextField
                  inputProps={{
                    multiple: false,
                    accept: "image/*",
                  }}
                  size="small"
                  placeholder={displayName}
                  type="file"
                  onChange={(e) => handleFileChange(e)}
                  options={options}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon>article</Icon>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <input id={`dff_${fieldRef}`} type="hidden" name={fieldRef} value={capturedData} />
    </Box>
  );
}

// Typechecking props of the Transaction
GeoTaggedImageUploadField.propTypes = {
  fieldRef: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.any),
};

GeoTaggedImageUploadField.defaultProps = {
  options: null,
};

export default GeoTaggedImageUploadField;
